<template>
    <div v-resize @resize="resizeHandler" class="content-notes-editor">
      <div class="content-notes-editor__header">
        <div class="content-notes-editor__title">{{title}} <span class="content-notes-editor__title-extra">autosave enabled</span></div>
        <base-material-design-icon class="content-notes-editor__collapse-button"
                                   :icon="isCollapsed ? 'expand' : 'collapse'"
                                   @click="changeCollapsedState" />
      </div>
       <div ref="editor" :class="{'content-notes-editor__wrapper-inited': inited}" class="content-notes-editor__wrapper">
         <editor
           :key="selectedClient._id"
           v-model="summary"
           :init="{
              plugins: 'advlist autolink lists link image charmap print preview anchor searchreplace visualblocks code fullscreen insertdatetime media table paste code help wordcount',
              toolbar: 'undo redo | formatselect | bold italic backcolor | link | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | removeformat | help',
              menubar: false,
              statusbar: false,
              height: '300px',
              setup: function (editor) {
                tinyMCE = editor
                editor.on('init', editorInit.bind(this))
                editor.on('undo', undoCallback.bind(this))
              }
            }"
         ></editor>
       </div>
      <base-loader v-if="!inited" class="content-notes-editor__loader" />
      <div v-else class="content-notes-editor__footer">
        <button @click.stop="manualApply" class="content-notes-editor__cancel-button">Close</button>
        <!-- <button @click.stop="manualApply" class="content-notes-editor__update-button">Save</button> -->
      </div>
      <div ref="styleBlock"></div>
    </div>
</template>

<script>
import { mapState } from 'vuex'
import Editor from '@tinymce/tinymce-vue'
// eslint-disable-next-line no-unused-vars
import ContentBrief from './index'
import _ from 'lodash'

/**
 * Component for editing content notes.
 * Based on tinymce editor {@see https://www.tiny.cloud/docs/}
 * Used in {@link ContentBrief}
 */
export default {
  name: 'ContentNotesEditor',
  components: {
    editor: Editor
  },
  props: {
    editorField: {
      type: String,
      required: true
    }
  },
  data () {
    return {
      isCollapsed: false,
      summary: '',
      inited: false,
      initialPayload: null,
      isDirty: false,
      tinyMCE: null,
      isSaving: false,
    }
  },
  watch: {
    briefContent: {
      handler (value) {
        if (!this.isSaving) {
          this.summary = value[this.editorField]
        }
        this.$nextTick(() => {
          this.isSaving = false
        })
      },
      immediate: true
    },
    summary: {
      handler () {
        this.debounceSummary()
        if (!this.isDirty) {
          const newPayload = this.buildPayload()
          this.isDirty = !_.isEqual(this.initialPayload, newPayload)
        }
      },
      deep: true
    },
  },
  computed: {
    ...mapState('briefContent', ['briefContent']),
    title () {
      return this.editorField === 'summary' ? 'Strategy Notes Editor' : 'Content Notes Editor'
    }
  },
  mounted () {
    this.initialPayload = this.buildPayload()
  },
  methods: {
    resizeHandler (event) {
      const blockHeight = event.detail.height
      this.$refs.editor.firstChild.setAttribute('style', `height:${blockHeight - 150}px;`)
      const iFrame = this.$refs.editor.firstChild.querySelector('iframe')
      iFrame && iFrame.setAttribute('style', `height:${blockHeight - 190}px; width: 100%; display:block`)
    },
    buildPayload () {
      const summary = this.summary
      const domElement = document.createElement('div')
      domElement.innerHTML = summary
      const links = domElement.querySelectorAll('a')
      links.forEach(el => {
        el.removeAttribute('rel')
      })
      const payload = {
        clientId: this.clientId,
        briefId: this.briefContent._id,
        [this.editorField]: domElement.innerHTML
      }
      return payload
    },
    debounceSummary: _.debounce(function() {
      this.onSave(true)
    }, 2000),
    manualApply() {
      this.onSave(true);
      this.close();
    },
    onSave (silentMode = false) {
      this.isSaving = true
      const payload = this.buildPayload()
      this.initialPayload = payload
      this.$emit('apply', {payload, silentMode})
    },
    resetUndo () {
      this.tinyMCE.undoManager.clear()
      this.isDirty = false
    },
    close () {
      const payload = this.buildPayload()
      this.$emit('close', payload)
    },
    changeCollapsedState () {
      this.isCollapsed = !this.isCollapsed
      this.$emit('changeCollapsed', this.isCollapsed)
    },
    undoCallback (val) {
      if (!this.tinyMCE.undoManager.hasUndo()) {
        this.isDirty = false
      }
    },
    editorInit () {
      this.inited = true
      const html = `
        <style>
          .mce-menu {
            position: fixed !important;
          }
          .mce-tooltip {
            position: fixed !important;
          }
        </style>
      `

      this.$refs.styleBlock.innerHTML = html
    }
  }

}
</script>

<style lang="scss">

  .content-notes-editor {
    padding:  0;
    height: 100%;

    &__header {
      padding: 10px;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    &__title {
      font-size: 18px;
      font-weight: 500;
      line-height: 1.67;
      color: $rfx-dark-march;
      white-space: nowrap;
    }

    &__title-extra {
      color: $rfx-light-march;
      font-size: 14px;
    }

    &__collapse-button {
      font-size: 18px;
      cursor: pointer;
    }

    &__wrapper {
      height: 0px;
      overflow: hidden;

      &-inited {
        height: auto;
      }
    }

    &__loader {
      margin: auto;
      display: flex !important;
      align-items: center;
      height: 340px !important;
    }

    &__footer {
      margin-top: 10px;
      display: flex;
      justify-content: flex-end;
      padding: 20px;
    }

    &__cancel-button,
    &__update-button {
      text-transform: uppercase;
      color: white;
      width: 160px;
      font-size: 13px;
      font-weight: 500;
      height: 50px;
      border-radius: 4px;
      background-color: $rfx-medium-march;
      border: none;
      &:disabled {
        pointer-events: none;
        opacity: .4;
      }
    }

    &__cancel-button {
      color: $rfx-dark-march;
      border-radius: 4px;
      background-color: white;
      position: inherit;
      border: none;
      margin-right: 30px;
      border: 1px solid $rfx-silver;

      &:hover {
        background-color: $rfx-almost-white;
      }
    }
  }
  /*overriding library classes*/
  .tox-notifications-container {
    display: none;
  }

  .content-notes-editor__wrapper {
    .tox-toolbar {
      background: rgba(1, 22, 39, 0.1) !important;
      padding: 10px 15px !important;
    }

    .tox.tox-tinymce {
      width: auto !important;
      height: auto;
      border: none !important;
    }

    .tox-edit-area {
      border-top: none !important;
      border-left: 1px solid rgba(1, 22, 39, 0.1) !important;
      border-right: 1px solid rgba(1, 22, 39, 0.1) !important;
      border-bottom: 1px solid rgba(1, 22, 39, 0.1) !important;
    }

    .tox-icon > svg {
      fill: #8b959c !important;
    }

    .tox-tbtn.tox-tbtn--enabled {
      background: none !important;

      svg {
        fill: $rfx-bright-turquoise !important;
      }
    }
  }

  button:focus {
    outline: 0;
  }

  button:hover {
    cursor: pointer;
  }
</style>
